import React, { useState, useEffect, useRef, useCallback } from "react";
import { Accordion, Button } from "react-bootstrap";
import "./OpalReminder.css";
import TauriImage from "../../../TauriImage/TauriImage";
import TruncatedText from "./TruncateText/TruncateText";
import TauriLoader2 from "../../../TauriLoader2/TauriLoader2";
import { addRecord, getAll, getNextId, getRecordById, upsertRecordsBySessionId } from "../../../../localDB";
import OpalCreateReminder from "./OpalCreateReminder/OpalCreateReminder";
import OpalCreateNotes from "./OpalCreateNotes/OpalCreateNotes";
import appURL from '../../../../appURL.json';
import { transformSessionNotesData, updateSessionNotesData } from "../../CoverageFactoryData/CoverageFactoryData";
import { useBaseProvider } from "../../../../BaseManager/BaseManager";
import OpalAlertModal from "../../../OpalAlertModal/OpalAlertModal";
import { accountMapping, sessionType, statusMapping, SyncStatusMap } from "../../../../visualizations/visualizations";

const OpalReminder = ({ section, id }) => {
    const lastSavedRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [groupedReminders, setGroupedReminders] = useState({});
    const [session, setSession] = useState(null);
    const [showValidation, setShowValidation] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const { isSync } = useBaseProvider();
    const isOnline = JSON.parse(localStorage.getItem("isOnline"));
    const hasInternet = navigator.onLine;

    const fetchData = useCallback(async () => {
        try {

            let sessionNotes = [];
            const sess = await getRecordById("session", id);

            if (isOnline && hasInternet) {
                const cust = await getRecordById("customer", sess?.AccountID);
                const accountId = sess?.AccountID;

                if (!accountId) {
                    sessionNotes = [];
                } else {
                    const token = localStorage.getItem("token");
                    let fullUrl = null;

                    if (sess?.Type === sessionType.Incidental && cust.Type === accountMapping.Default) {
                        fullUrl = `${appURL.API_SessionNotes}/${id}`;
                    } else if (accountId) {
                        fullUrl = `${appURL.API_AccountNotes}/${accountId}`;
                    }

                    const response = await fetch(fullUrl, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                    });

                    if (!response.ok) {
                        console.error('Failed to fetch notes!');
                        return;
                    }

                    const data = await response.json();
                    const trasformData = await transformSessionNotesData(data?.notes, id || []);
                    await upsertRecordsBySessionId("sessionNotes", id, trasformData);
                    sessionNotes = trasformData;
                }
            } else {
                const sn = await getAll("sessionNotes");
                sessionNotes = sn.filter((item) => item.SessionID === id && item.SyncStatus !== SyncStatusMap.done);
            }

            setData(sessionNotes || []);
            setSession(sess);
        } catch (error) {
            console.log(error);
        }
    }, [id]);


    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                await fetchData();
            } catch (error) {
                console.error("Error during initial data fetch:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [id]);

    useEffect(() => {
        const handleRefresh = () => {
            fetchData();
        };

        window.addEventListener("refreshSession", handleRefresh);

        return () => {
            window.removeEventListener("refreshSession", handleRefresh);
        };
    }, [fetchData]);

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Helper function to group reminders by date
    const groupRemindersByDate = (data) => {
        const map = data.reduce((acc, item) => {
            const dateSource = item.ContentType === 1 ? item.CreatedDate : item.ReminderDate;

            const key = new Date(dateSource).toLocaleDateString('en-CA');
            (acc[key] ||= []).push(item);
            return acc;
        }, {});

        return Object.fromEntries(
            Object.entries(map).sort(([a], [b]) => new Date(a) - new Date(b))
        );
    };

    useEffect(() => {
        const grouped = groupRemindersByDate(data);
        setGroupedReminders(grouped);
    }, [data]);

    const [selectedDateIndex, setSelectedDateIndex] = useState(0);
    const containerRef = useRef(null);
    const itemRefs = useRef([]);

    useEffect(() => {
        const handleScroll = () => {
            const containerTop = containerRef.current.getBoundingClientRect().top;

            const visibleIndex = itemRefs.current.findIndex((ref) => {
                if (!ref) return false;
                const rect = ref.getBoundingClientRect();
                return rect.top - containerTop >= 0 && rect.top - containerTop < 100;
            });

            if (visibleIndex !== -1 && visibleIndex !== selectedDateIndex) {
                setSelectedDateIndex(visibleIndex);
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }
    }, [selectedDateIndex]);

    const handleClick = (index) => {
        setSelectedDateIndex(index);
        itemRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleSave = async (newNote) => {
        try {
            setIsCreating(true);

            const noteKey = `${newNote.ID}-${newNote.ReminderDate}`;
            if (lastSavedRef.current === noteKey) {
                return; // Prevent saving the exact same note twice
            }

            if (isOnline && hasInternet) {
                const token = localStorage.getItem("token");
                const devId = localStorage.getItem("devId");

                const requestBody = {
                    id,
                    content: newNote.Content,
                    ...(newNote.ContentType === 2 && {
                        date: newNote.ReminderDate,
                        location: newNote.ReminderLocation
                    })
                };

                const response = await fetch(appURL.API_AddSessionNotes, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        "X-ETR-Device-ID": devId
                    },
                    body: JSON.stringify(requestBody)
                });

                if (!response.ok) {
                    setValidationMessage("Failed to add notes!");
                    setShowValidation(true);
                    return;
                }
            } else {
                await addRecord('sessionNotes', newNote);
            }

            lastSavedRef.current = noteKey;

            setShowNotesModal(false);
            setShowReminderModal(false);
            await fetchData();
        } catch (error) {
            setValidationMessage("Failed to add notes");
            setShowValidation(true);
        } finally {
            setIsCreating(false);
        }

    }

    return (
        <div className="opal-reminder mb-2">
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header className="p-0 mb-2">
                        <div className="header-container d-flex flex-column flex-md-row justify-content-between align-items-start">
                            <div className="header-left">
                                <TauriImage localImage={section?.icon} className="tat-icon me-2" alt="icon" />
                                <span className="header-title">{section?.header?.toUpperCase()}</span>
                            </div>
                            <div className="d-flex flex-column flex-md-row justify-content-start">
                                <Button
                                    className="btn d-flex align-items-center p-0 ms-0 me-0 ms-md-auto my-md-0 my-1 me-md-4 "
                                    size="sm"
                                    disabled={isSync}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowNotesModal(true);
                                    }}
                                >
                                    <TauriImage localImage="/Images/flat-color-icons--plus.svg" className="opalrem-icon me-2" alt="icon" />
                                    <div>Add Notes</div>
                                </Button>
                                <Button
                                    className="btn d-flex align-items-center p-0 ms-0 me-0 ms-md-auto my-md-0 my-1 me-md-4"
                                    size="sm"
                                    disabled={isSync}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowReminderModal(true);
                                    }}
                                >
                                    <TauriImage localImage="/Images/flat-color-icons--plus.svg" className="opalrem-icon me-2" alt="icon" />
                                    <div>Add Reminder</div>
                                </Button>
                            </div>
                        </div>
                    </Accordion.Header>

                    {/* Scroll Spy */}
                    <Accordion.Body className="py-0 px-0">
                        <div className="opal-reminder-body">
                            {loading ? <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" /> : (
                                <>
                                    {/* Sidebar Nav */}
                                    <nav className="nav position-sticky d-none d-md-block">
                                        {Object.keys(groupedReminders).map((date, index) => (
                                            <button
                                                key={index}
                                                className={`nav-link text-start btn btn-link ${index === selectedDateIndex ? "selected" : ""}`}
                                                onClick={() => handleClick(index)}
                                                style={{ textDecoration: "none" }}
                                            >
                                                {formatDate(date)}
                                            </button>
                                        ))}
                                    </nav>

                                    {/* Scrollable Content */}
                                    <div
                                        className="scrollable-content"
                                        ref={containerRef}
                                        tabIndex="0"
                                    >
                                        {Object.entries(groupedReminders).map(([date, group], index) => (
                                            <div
                                                key={index}
                                                ref={(el) => (itemRefs.current[index] = el)}
                                                className="mb-4"
                                            >
                                                <div className="date-display">{formatDate(date)}</div>

                                                {group.map((item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="note-item d-flex align-items-start p-2"
                                                        style={{ backgroundColor: idx % 2 === 0 ? '#F8F8F8' : '#FFFFFF' }}
                                                    >
                                                        <TauriImage
                                                            localImage={
                                                                item?.ContentType === 2
                                                                    ? "/Images/akar-icons--bell.svg"
                                                                    : "/Images/notes.svg"
                                                            }
                                                            className="note-icon me-3"
                                                            color="#000000"
                                                            alt="icon"
                                                        />
                                                        <div>
                                                            <TruncatedText text={item?.Content} maxLength={100} />
                                                            {item?.ReminderLocation && <div>{item.ReminderLocation}</div>}
                                                        </div>
                                                    </div>
                                                ))}

                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <OpalCreateNotes
                show={showNotesModal}
                onHide={() => setShowNotesModal(false)}
                sessionId={id}
                onSave={handleSave}
                isCreating={isCreating}
            />
            <OpalCreateReminder
                show={showReminderModal}
                onHide={() => setShowReminderModal(false)}
                sessionId={id}
                onSave={handleSave}
                isCreating={isCreating}
            />
            <OpalAlertModal
                show={showValidation}
                onHide={() => {
                    setShowValidation(false);
                }}
                title={"Message"}
                bodyText={validationMessage}
                modalType="message"
            />
        </div>
    );
};

export default OpalReminder;
